import type { EditorCore } from "@loumy/contracts";
import { useEffect, useRef } from "react";
import { useEditorStore } from "./editorStore";
import { documentNameOf, documentPath, EDITOR_MANIFEST_FILE, parseDocument } from "./project";
import { useProjectStore } from "./projectStore";
import type { ProjectHostApi } from "./useProjectHost";

// D78 — BIDIREKTIONALER Live-Sync zwischen lokalem Projekt und Editor (schließt den D69/D72-Gap „kein
// Auto-Watch→Reload im MVP"). EINMAL gemountet (über die EditorMenuStrip unter den Tabs, D87); selbst-
// guardend (no-op ohne `documentSet`/ohne verbundene Quelle), daher auch für Single-Doc-Editoren sicher.
//   • Editor → Ordner: debounced Auto-Save des aktiven Dokuments (kein manueller Save-Klick mehr nötig).
//   • Ordner → Editor: CliSource via WS-`watch` (live), FileSystemAccess via Polling (Browser-API ohne Watch).
//     Manifest/Assets-Änderung → `bumpDataEpoch` (Registry/Bilder neu); Quelldatei-Änderung → `generate()`
//     (CLI) → Manifest neu; aktives Dokument extern geändert → Re-Open (nur wenn NICHT dirty; eigene
//     Auto-Save-Echos werden unterdrückt, kein Überschreiben ungespeicherter Edits).

const AUTOSAVE_MS = 600;
const POLL_MS = 1500;
const SELF_WRITE_IGNORE_MS = 2500;
const MANIFEST = EDITOR_MANIFEST_FILE; // geteilte Konstante (Loader == Collision-Mirror == Sync, Codex-P1)

const snap = (doc: unknown): string => JSON.stringify(doc ?? null);
const norm = (p: string): string => p.replace(/^\.?\//, "");
const isSourceFile = (p: string): boolean => /\.(tsx?|jsx?)$/.test(p) || /\.(behavior|placeable|project)\.toml$/.test(p);
const isAssetFile = (p: string): boolean => p.startsWith("assets/");
const isManifest = (p: string): boolean => p === MANIFEST || p.endsWith(`/${MANIFEST}`);

export function useProjectSync(core: EditorCore, host: ProjectHostApi): void {
  const docSet = core.documentSet;
  const source = host.source;
  const currentDoc = useEditorStore((s) => s.docs[core.id]);
  const docSnap = snap(currentDoc); // ändert sich je Edit → debounce-Reset

  const hostRef = useRef(host);
  hostRef.current = host;
  const lastSelfWrite = useRef<{ path: string; at: number } | null>(null);
  const now = () => (typeof performance !== "undefined" ? performance.now() : 0);

  // ── Editor → Ordner: debounced Auto-Save ─────────────────────────────────────
  useEffect(() => {
    if (!host.dirty || !host.canWrite || !host.activeDocument || !docSet) return;
    const path = documentPath(docSet, host.activeDocument);
    const t = setTimeout(() => {
      lastSelfWrite.current = { path, at: now() }; // markiert den eigenen Write → Echo-Unterdrückung
      void hostRef.current.saveDocument();
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host.dirty, host.canWrite, host.activeDocument, docSnap, docSet]);

  // ── Ordner → Editor: Live-Watch (CLI) ────────────────────────────────────────
  useEffect(() => {
    if (!source || !source.capabilities.watch || typeof source.watch !== "function") return;
    const apply = (rawPaths: string[]) => {
      const st = useProjectStore.getState();
      let manifest = false;
      let generate = false;
      let reopen = false;
      for (const raw of rawPaths) {
        const p = norm(raw);
        if (isManifest(p) || isAssetFile(p)) manifest = true;
        else if (docSet && st.activeDocument && documentNameOf(docSet, p) === st.activeDocument) {
          const lw = lastSelfWrite.current;
          const echo = lw && lw.path === p && now() - lw.at < SELF_WRITE_IGNORE_MS;
          if (!echo) reopen = true;
        } else if (isSourceFile(p)) generate = true;
      }
      if (generate && source.capabilities.generate && source.generate) {
        void source
          .generate()
          .then(() => useProjectStore.getState().bumpDataEpoch())
          .catch(() => {});
      }
      if (manifest) useProjectStore.getState().bumpDataEpoch();
      if (reopen && !hostRef.current.dirty && st.activeDocument) void hostRef.current.openDocument(st.activeDocument);
    };
    try {
      return source.watch("**", apply);
    } catch {
      return undefined; // Watch nicht verfügbar → still degradieren
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, docSet]);

  // ── Ordner → Editor: Polling-Fallback (FileSystemAccess hat keine Watch-API) ──
  useEffect(() => {
    if (!source || source.capabilities.watch || typeof source.list !== "function") return; // CLI nutzt Watch
    let cancelled = false;
    let lastManifest: string | null = null;
    const tick = async () => {
      if (cancelled || !source) return;
      const st = useProjectStore.getState();
      // Manifest-Änderung → Registry/Bilder neu.
      try {
        const bytes = await source.readFile(MANIFEST);
        const text = new TextDecoder().decode(bytes);
        if (lastManifest !== null && text !== lastManifest) useProjectStore.getState().bumpDataEpoch();
        lastManifest = text;
      } catch {
        /* kein Manifest (noch) — ignorieren */
      }
      // Aktives Dokument extern geändert (und NICHT dirty) → Re-Open.
      const h = hostRef.current;
      if (docSet && st.activeDocument && !h.dirty) {
        try {
          const bytes = await source.readFile(documentPath(docSet, st.activeDocument));
          const parsed = parseDocument(bytes, core.documentSchema);
          if (!cancelled && snap(parsed) !== st.savedSnapshot) void h.openDocument(st.activeDocument);
        } catch {
          /* Datei (noch) nicht lesbar — ignorieren */
        }
      }
    };
    const id = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, docSet, core]);
}
